import type { IApiClient } from '../index'
import type { TrackedJob } from '../contexts/GenerationContext'

/** What deleting a GCC design will remove and stop, worked out before the user confirms. */
export interface GccDesignDeletePlan {
  designId: string
  /** Every id in the design's derivedTnProjectIds: TN Analyses and combined dashboards. */
  derivedIds: string[]
  tnAnalysisCount: number
  combinedCount: number
  /** Derived projects whose Inside-Out job is still pending or running. */
  runningCount: number
  /** Inside-Out jobs to cancel before anything is deleted. */
  cancelJobIds: string[]
}

const isActive = (j: Pick<TrackedJob, 'status'>) => j.status === 'pending' || j.status === 'running'

/**
 * Read the design and each project derived from it via "Run TN Analysis" (WI 2494).
 * The project list does not carry derivedTnProjectIds, gccCombined or gccTnJobId
 * (CosmosDB LIST_FIELDS), so each project is fetched. A derived project that is
 * gone or unreadable is left out of the counts but is still deleted.
 */
export async function planGccDesignDelete(
  api: Pick<IApiClient, 'getProject'>,
  designId: string,
  activeJobs: Pick<TrackedJob, 'jobId' | 'projectId' | 'type' | 'status'>[],
): Promise<GccDesignDeletePlan> {
  const design = await api.getProject(designId)
  const derivedIds = design?.derivedTnProjectIds ?? []
  const derived = await Promise.all(derivedIds.map((id) => api.getProject(id).catch(() => null)))
  const found = derived.filter((p) => p !== null)
  const running = activeJobs.filter((j) => j.type === 'inside-out' && isActive(j) && derivedIds.includes(j.projectId))
  const jobIds = [
    ...found.map((p) => p.gccTnJobId).filter((id): id is string => !!id),
    ...running.map((j) => j.jobId),
  ]
  return {
    designId,
    derivedIds,
    tnAnalysisCount: found.filter((p) => !p.gccCombined).length,
    combinedCount: found.filter((p) => !!p.gccCombined).length,
    runningCount: new Set(running.map((j) => j.projectId)).size,
    cancelJobIds: [...new Set(jobIds)],
  }
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** The confirmation text: today's wording when nothing else goes with the design. */
export function gccDesignDeleteMessage(plan: GccDesignDeletePlan): string {
  const base = 'Delete this GCC design? This cannot be undone.'
  const parts = [
    plan.tnAnalysisCount > 0 ? plural(plan.tnAnalysisCount, 'TN Analysis', 'TN Analyses') : '',
    plan.combinedCount > 0 ? plural(plan.combinedCount, 'combined dashboard', 'combined dashboards') : '',
  ].filter(Boolean)
  if (parts.length === 0) return base
  const running = plan.runningCount > 0
    ? `\n\n${plan.runningCount} of them ${plan.runningCount === 1 ? 'is' : 'are'} still running and will be stopped.`
    : ''
  return `${base}\n\nThis also deletes ${parts.join(' and ')} built from it.${running}`
}

/**
 * Delete a GCC design and every project derived from it. Running Inside-Out jobs
 * are cancelled first, and all cancels finish before anything is deleted, so a
 * running TN Analysis stops writing. Derived deletions are best-effort: failures
 * are swallowed so the design delete still lands. A failed design delete rejects.
 */
export async function deleteGccProject(
  api: Pick<IApiClient, 'deleteProject' | 'cancelInsideOutJob'>,
  plan: GccDesignDeletePlan,
): Promise<void> {
  await Promise.all(plan.cancelJobIds.map((id) => api.cancelInsideOutJob(id).catch(() => {})))
  await Promise.all([
    api.deleteProject(plan.designId),
    ...plan.derivedIds.map((id) => api.deleteProject(id).catch(() => {})),
  ])
}
